import { AttributeClient, AttributeValueClient } from '@frontend/attribute/api';
import { Attribute, AttributeValue } from '@frontend/attribute/types';
import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { AllowanceClient } from '@frontend/budget/api';
import { BudgetAllowance, UserAllowance } from '@frontend/budget/types';
import { CertificationClient } from '@frontend/certificate/api';
import { Certification, UserCertification } from '@frontend/certificate/types';
import { EntityType } from '@frontend/common';
import { Feature } from '@frontend/functionality/types';
import { IGroup } from '@frontend/group/types';
import { Group } from '@frontend/group/utils';
import { useLocale } from '@frontend/lang';
import { ProductAttributeClient, ProductConsumeClient, ProductOrderClient } from '@frontend/product/api';
import { IProduct, ProductAttribute, ProductType } from '@frontend/product/types';
import { useBlobRepository } from '@frontend/repository';
import { StockAvailabilityClient } from '@frontend/stock/api';
import { ProductAvailability, StockAvailability } from '@frontend/stock/types';
import { useEffect, useMemo, useRef, useState } from 'react';

import ImagePlaceholder from '../../../../../assets/images/image-placeholder.png';
import { useScreen } from '../../useScreen';
import { LeafCategoryViewProps } from './leaf-category-view.component';

// Helper function to check if item is a product (not a category)
function isProduct(p: IProduct | IGroup): p is IProduct {
    return !(p as IGroup).node_type;
}

interface AttributeOption {
    attribute: Attribute;
    values: AttributeValue[];
}

interface ProductWithProductAttributes extends IProduct {
    product_attributes: ProductAttribute[];
}

export const useLeafCategoryView = (props: LeafCategoryViewProps) => {
    const { navigationState, store, wizardData } = useScreen();
    const { userOverwrite } = wizardData;
    const isRecycleMode = wizardData.browseProductsContext?.feature === Feature.RECYCLE_TRANSACTION;
    const { locale } = useLocale();
    const blobRepository = useBlobRepository();
    const [image, setImage] = useState<string | null>(null);
    const [products, setProducts] = useState<ProductWithProductAttributes[]>([]);
    const [productStockMap, setProductStockMap] = useState<ProductAvailability>();
    const [allowances, setAllowances] = useState<UserAllowance | null>(null);
    const [certifications, setCertifications] = useState<UserCertification | null>(null);
    const [attributeOptions, setAttributeOptions] = useState<AttributeOption[]>([]);
    const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [orderableProductIds, setOrderableProductIds] = useState<Set<string>>(new Set());
    const hasRestoredProductSelection = useRef(false);
    const canOrderProducts = store?.data?.canOrderProducts === true;

    useEffect(() => {
        const selectedLeafProduct = store?.data?.browseProductsNavigation?.leafSelectedProduct as IProduct | null | undefined;
        if (hasRestoredProductSelection.current || !selectedLeafProduct || products.length === 0) return;

        const product = products.find((candidate) => candidate.id === selectedLeafProduct.id);
        if (!product) return;

        setSelectedAttributes(Object.fromEntries(product.product_attributes.map(({ attribute_id, value_id }) => [attribute_id, value_id])));
        setAmount(store?.data?.selectedAmount ?? 1);
        hasRestoredProductSelection.current = true;
    }, [products, store?.data?.browseProductsNavigation?.leafSelectedProduct, store?.data?.selectedAmount]);

    useEffect(() => {
        if (!props.category) return;
        new Group(props.category).getBlobImage(blobRepository, EntityType.PRODUCT_CATEGORY).then((image) => {
            if (!image) setImage(null);
            else setImage(image);
        });
    }, [props.category]);

    useEffect(() => {
        const fetchData = async () => {
            if (!navigationState.user) return;

            setIsLoading(true);

            const spotId = navigationState.spot?.id;
            const accountId = navigationState.user.accountId;
            const certificationUserId = userOverwrite?.id ?? navigationState.user.id;

            const [consumeLendResponse, orderResponse] = await Promise.all([
                ProductConsumeClient.fetchCategoryConsumeLendProducts(
                    accountId,
                    props.category.id,
                    { sort_field: 'order', sort_direction: 'asc' },
                    navigationState.user.auth
                ),
                canOrderProducts
                    ? ProductOrderClient.fetchCategoryOrderProducts(
                          accountId,
                          props.category.id,
                          { sort_field: 'order', sort_direction: 'asc' },
                          navigationState.user.auth
                      )
                    : Promise.resolve({ results: [] as (IProduct | IGroup)[] })
            ]);
            const orderableProducts = orderResponse.results.filter(
                (element): element is IProduct => isProduct(element) && element.type === ProductType.CONSUMABLE
            );
            const products = Array.from(
                new Map(
                    [...consumeLendResponse.results.filter((element): element is IProduct => isProduct(element)), ...orderableProducts].map((product) => [
                        product.id,
                        product
                    ])
                ).values()
            ).filter((product) => !isRecycleMode || product.type !== ProductType.KIT);
            setOrderableProductIds(new Set(orderableProducts.map((product) => product.id)));
            const productIds = products.map((p) => p.id);
            const equipmentProductIds = products.filter((product) => product.type === ProductType.EQUIPMENT).map((product) => product.id);
            const iotTokenPromise =
                !isRecycleMode && equipmentProductIds.length > 0 ? DriverAuthenticationManager.getInstance().waitForToken() : Promise.resolve(null);

            // Fetch stock from all spots (we'll determine current spot vs other spots in UI)
            const [productStock, userAllowances, userCertifications] = await Promise.all([
                isRecycleMode ? Promise.resolve(undefined) : StockAvailabilityClient.postGetProductAvailability(accountId, productIds),
                !isRecycleMode && spotId
                    ? AllowanceClient.postGetUserAllowancesV2(
                          productIds,
                          { spot_id: spotId, user_id: userOverwrite?.id ?? undefined },
                          navigationState.user!.auth
                      )
                    : Promise.resolve(null),
                !isRecycleMode && equipmentProductIds.length > 0
                    ? iotTokenPromise
                          .then((iotToken) =>
                              iotToken
                                  ? CertificationClient.postGetProductCertification(accountId, certificationUserId, equipmentProductIds, iotToken)
                                  : ({} as UserCertification)
                          )
                          .catch((): UserCertification => ({}))
                    : Promise.resolve(null)
            ]);

            setProductStockMap(productStock);
            setAllowances(userAllowances);
            setCertifications(userCertifications);

            const productAttributesResponse = await ProductAttributeClient.resolveProductAttributes(
                products.map((p) => p.id),
                'product_id'
            );
            const productAttributes = productAttributesResponse.results;

            const productsWithAttributes: ProductWithProductAttributes[] = products.map((product) => ({
                ...product,
                product_attributes: productAttributes.filter((pa) => pa.product_id === product.id)
            }));

            const attributeIds = new Set<string>();
            const valueIds = new Set<string>();
            productAttributes.forEach((pa) => {
                attributeIds.add(pa.attribute_id);
                valueIds.add(pa.value_id);
            });

            const [attributesResponse, valuesResponse] = await Promise.all([
                AttributeClient.resolveAttributes(Array.from(attributeIds)),
                AttributeValueClient.resolveAttributeValues(Array.from(valueIds))
            ]);

            const options: AttributeOption[] = attributesResponse.results.map((attribute) => ({
                attribute,
                values: valuesResponse.results.filter((v) => v.attribute_id === attribute.id).sort((a, b) => (a.order || 0) - (b.order || 0))
            }));

            setProducts(productsWithAttributes);
            setAttributeOptions(options);
            setIsLoading(false);
        };

        fetchData();
    }, [canOrderProducts, props.category, navigationState.user, navigationState.spot?.id, userOverwrite?.id, isRecycleMode]);

    // Get current spot ID for stock filtering
    const currentSpotId = navigationState.spot?.id;

    // Helper function to get total stock quantity for a product (all spots)
    const getTotalStockQuantity = (productId: string): number => {
        const stockAvailability = productStockMap?.[productId];
        if (!stockAvailability) return 0;
        return stockAvailability.quantity;
    };

    // Helper function to check if user has allowance for a product
    const hasAllowance = (productId: string): boolean => {
        const allowance = allowances?.[productId];
        if (!allowance) return false;
        return allowance.allowed && allowance.quantity > 0;
    };

    const getCertification = (productId: string): Certification | null => {
        return certifications?.[productId] ?? null;
    };

    const isCertified = (product: IProduct): boolean => {
        if (isRecycleMode || product.type !== ProductType.EQUIPMENT) return true;
        return getCertification(product.id)?.certified === true;
    };

    const isOptionAvailable = (attributeId: string, valueId: string): boolean => {
        const testSelection = { ...selectedAttributes, [attributeId]: valueId };

        return products.some((product) => {
            if (!isRecycleMode) {
                // Check if user has allowance for this product
                if (!hasAllowance(product.id)) return false;
                const canOrder = product.type === ProductType.CONSUMABLE && orderableProductIds.has(product.id);
                if (!canOrder && !isCertified(product)) return false;
                // Check if there's any stock (in any spot) - disable if no stock anywhere
                if (!canOrder && getTotalStockQuantity(product.id) <= 0) return false;
            }

            return Object.entries(testSelection).every(([attrId, valId]) => {
                return product.product_attributes.some((pa) => pa.attribute_id === attrId && pa.value_id === valId);
            });
        });
    };

    const selectAttribute = (attributeId: string, valueId: string) => {
        setSelectedAttributes((prev) => {
            const newSelection = { ...prev };

            if (newSelection[attributeId] === valueId) {
                delete newSelection[attributeId];
            } else {
                newSelection[attributeId] = valueId;
            }

            return newSelection;
        });
    };

    // Find the product that matches the selected attributes (regardless of stock location)
    const matchedProduct = useMemo(() => {
        return products.find((product) => {
            if (product.product_attributes.length === 0) return false;

            const allProductAttributesSelected = product.product_attributes.every((pa) => {
                return selectedAttributes[pa.attribute_id] === pa.value_id;
            });

            if (!allProductAttributesSelected) return false;

            const productAttributeIds = new Set(product.product_attributes.map((pa) => pa.attribute_id));
            const selectedAttributeIds = Object.keys(selectedAttributes);

            return selectedAttributeIds.every((attrId) => productAttributeIds.has(attrId));
        });
    }, [products, selectedAttributes]);

    // Calculate stock availability for the matched product
    const stockInfo = useMemo(() => {
        if (!matchedProduct || !productStockMap) {
            return { hasStockInCurrentSpot: false, hasStockInOtherSpots: false, currentSpotStock: null, totalStock: null, otherSpotIds: [] };
        }

        const stockAvailability = productStockMap[matchedProduct.id];
        if (!stockAvailability || !stockAvailability.stocks) {
            return { hasStockInCurrentSpot: false, hasStockInOtherSpots: false, currentSpotStock: null, totalStock: stockAvailability, otherSpotIds: [] };
        }

        const currentSpotStocks = stockAvailability.stocks.filter((s) => s.spot_id === currentSpotId);
        const otherSpotStocks = stockAvailability.stocks.filter((s) => s.spot_id !== currentSpotId);

        const currentSpotQuantity = currentSpotStocks.reduce((sum, s) => sum + s.quantity, 0);
        const otherSpotQuantity = otherSpotStocks.reduce((sum, s) => sum + s.quantity, 0);

        // Get unique spot IDs from other spots that have stock
        const otherSpotIds = [...new Set(otherSpotStocks.filter((s) => s.quantity > 0).map((s) => s.spot_id))];

        // Create a StockAvailability object for current spot only
        const currentSpotStockAvailability: StockAvailability | null =
            currentSpotStocks.length > 0
                ? {
                      available: currentSpotQuantity > 0,
                      quantity: currentSpotQuantity,
                      stocks: currentSpotStocks
                  }
                : null;

        return {
            hasStockInCurrentSpot: currentSpotQuantity > 0,
            hasStockInOtherSpots: otherSpotQuantity > 0,
            currentSpotStock: currentSpotStockAvailability,
            totalStock: stockAvailability,
            otherSpotIds
        };
    }, [matchedProduct, productStockMap, currentSpotId]);

    // Current product is only valid if there's stock in current spot
    const currentProduct = useMemo(() => {
        if (!matchedProduct) return null;
        const isOrderFallback =
            matchedProduct.type === ProductType.CONSUMABLE && orderableProductIds.has(matchedProduct.id) && !stockInfo.hasStockInCurrentSpot;
        if (!isRecycleMode && !isOrderFallback && !stockInfo.hasStockInCurrentSpot) return null;
        if (!isRecycleMode && !isOrderFallback && matchedProduct.type === ProductType.EQUIPMENT && certifications?.[matchedProduct.id]?.certified !== true)
            return null;
        return matchedProduct;
    }, [matchedProduct, stockInfo.hasStockInCurrentSpot, certifications, isRecycleMode, orderableProductIds]);

    // Calculate max budget for selected product
    const maxBudget = useMemo<BudgetAllowance | null>(() => {
        if (!currentProduct || !allowances) return null;
        return allowances[currentProduct.id] ?? null;
    }, [currentProduct, allowances]);

    // Calculate max stock for selected product (current spot only)
    const maxStock = useMemo<StockAvailability | null>(() => {
        if (!currentProduct || !stockInfo.currentSpotStock) return null;
        return stockInfo.currentSpotStock;
    }, [currentProduct, stockInfo.currentSpotStock]);

    // Calculate total max (minimum of budget and stock)
    const totalMax = useMemo<number | null>(() => {
        if (isRecycleMode) return 1;
        if (currentProduct?.type === ProductType.CONSUMABLE && orderableProductIds.has(currentProduct.id) && !stockInfo.hasStockInCurrentSpot)
            return maxBudget?.quantity ?? null;
        if (maxStock == null || maxBudget == null) return null;
        if (maxStock.quantity > maxBudget.quantity) return maxBudget.quantity;
        return maxStock.quantity;
    }, [maxStock, maxBudget, isRecycleMode, orderableProductIds, currentProduct?.type, stockInfo.hasStockInCurrentSpot]);

    // Reset amount when totalMax changes
    useEffect(() => {
        if (totalMax !== null && totalMax >= 1 && amount === 0) setAmount(1);
        if (totalMax !== null && totalMax < amount) setAmount(totalMax);
    }, [totalMax, amount]);

    // Check if amount change is disabled
    const isAmountDisabled = (addition: number): boolean => {
        if (amount + addition < 1 || totalMax == null || amount + addition > totalMax) return true;
        return false;
    };

    useEffect(() => {
        if (currentProduct) {
            setSelectedProduct(currentProduct);
            // Get all available stock items from current spot
            const isOrderFallback =
                currentProduct.type === ProductType.CONSUMABLE && orderableProductIds.has(currentProduct.id) && !stockInfo.hasStockInCurrentSpot;
            const stocks = isRecycleMode || isOrderFallback ? [] : (stockInfo.currentSpotStock?.stocks ?? []);
            props.onProductSelected?.(
                currentProduct,
                stocks,
                isRecycleMode ? 1 : amount,
                isRecycleMode ? null : maxBudget,
                isRecycleMode ? null : getCertification(currentProduct.id),
                orderableProductIds.has(currentProduct.id)
            );
        } else {
            setSelectedProduct(null);
            props.onProductSelected?.(null, [], 0, null, null, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProduct, stockInfo.currentSpotStock, amount, maxBudget, orderableProductIds]);

    return {
        products,
        attributeOptions,
        selectedAttributes,
        selectAttribute,
        isOptionAvailable,
        currentProduct,
        matchedProduct,
        selectedProduct,
        setSelectedProduct,
        image,
        locale,
        allowances,
        productStockMap,
        amount,
        setAmount,
        isAmountDisabled,
        maxBudget,
        maxStock,
        totalMax,
        stockInfo,
        certification: matchedProduct ? getCertification(matchedProduct.id) : null,
        isLoading,
        placeholder: ImagePlaceholder,
        isRecycleMode,
        isOrderFallback: currentProduct?.type === ProductType.CONSUMABLE && orderableProductIds.has(currentProduct.id) && !stockInfo.hasStockInCurrentSpot
    };
};
